import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal } from '@heartinternet/hi-ui';
import { Text } from '@heartinternet/hi-ui';
import { Select } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Spinner } from '@heartinternet/hi-ui';
import { Flex } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { Card } from '@heartinternet/hi-ui';
import type { Package } from '../types';

const SERVERS_ENDPOINT = '/cp/rpc/premium_hosting/list_premium_server_instances';
const SCHEDULE_LIST_ENDPOINT = '/cp/rpc/migration/list';
const SCHEDULE_POST_ENDPOINT = '/cp/rpc/migration/schedule';
const MAX_SITES = 5;
const MAX_DAYS = 7;

interface PremiumServer {
  name: string;
  oid: string;
  ip_address: string[];
}

interface ExistingMigration {
  status: string;
  scheduled_start: string;
}

interface MigrateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPackages: Package[];
  onMigrationScheduled: () => void;
}

function getAvailableDates(maxDays: number): Date[] {
  const dates: Date[] = [];
  for (let i = 0; i < maxDays; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    d.setMinutes(0, 0, 0);
    dates.push(d);
  }
  return dates;
}

function getAvailableHours(day: Date, existing: ExistingMigration[]): number[] {
  const now = new Date();
  const isToday =
    day.getDate() === now.getDate() &&
    day.getMonth() === now.getMonth() &&
    day.getFullYear() === now.getFullYear();
  const startHour = isToday ? now.getHours() + 1 : 0;

  const available: number[] = [];
  for (let h = startHour; h < 24; h++) {
    available.push(h);
  }

  // Remove hours with existing scheduled migrations
  const takenHours = existing
    .filter((m) => m.status === 'scheduled')
    .filter((m) => {
      const d = new Date(m.scheduled_start);
      return (
        d.getDate() === day.getDate() &&
        d.getMonth() === day.getMonth() &&
        d.getFullYear() === day.getFullYear()
      );
    })
    .map((m) => new Date(m.scheduled_start).getHours());

  return available.filter((h) => !takenHours.includes(h));
}

export function formatDate(d: Date): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const day = d.getDate();
  const suffix =
    day === 1 || day === 21 || day === 31
      ? 'st'
      : day === 2 || day === 22
        ? 'nd'
        : day === 3 || day === 23
          ? 'rd'
          : 'th';
  return `${days[d.getDay()]} ${day}${suffix} ${months[d.getMonth()]}`;
}

function formatDateValue(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatHour(h: number): string {
  return `${String(h).padStart(2, '0')}:00`;
}

export const MigrateModal: React.FC<MigrateModalProps> = ({
  isOpen,
  onClose,
  selectedPackages,
  onMigrationScheduled,
}) => {
  const [servers, setServers] = useState<PremiumServer[]>([]);
  const [existingMigrations, setExistingMigrations] = useState<ExistingMigration[]>([]);
  const [serversLoading, setServersLoading] = useState(true);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [serversError, setServersError] = useState<string | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [selectedServer, setSelectedServer] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedHour, setSelectedHour] = useState('');
  const [showSites, setShowSites] = useState(false);

  // Filter packages: exclude windows and already-premium (is_hemsx)
  const migratableSites = useMemo(() => {
    return selectedPackages
      .filter((p) => Number(p.is_hemsx) !== 1)
      .slice(0, MAX_SITES)
      .map((p) => ({
        oid: p.oid,
        domain: p.short_domain || p.login,
        windows: p.web_server_type === 'windows' ? 1 : 0,
      }));
  }, [selectedPackages]);

  const windowsSites = useMemo(() => migratableSites.filter((s) => s.windows), [migratableSites]);

  const availableDates = useMemo(() => getAvailableDates(MAX_DAYS), []);

  const currentDate = useMemo(() => {
    if (!selectedDate) return availableDates[0];
    const parts = selectedDate.split('-').map(Number);
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    return d;
  }, [selectedDate, availableDates]);

  const availableHours = useMemo(
    () => getAvailableHours(currentDate, existingMigrations),
    [currentDate, existingMigrations]
  );

  // Fetch data when modal opens
  /* eslint-disable react-hooks/set-state-in-effect -- data fetching effect legitimately sets loading state */
  useEffect(() => {
    if (!isOpen) return;

    setServersLoading(true);
    setScheduleLoading(true);
    setServersError(null);
    setScheduleError(null);
    setSubmitError(null);
    setSuccess(false);

    fetch(SERVERS_ENDPOINT)
      .then((r) => r.json())
      .then((data) => {
        const s = data.premium_servers || [];
        setServers(s);
        if (s.length > 0) setSelectedServer(s[0].oid);
        setServersLoading(false);
      })
      .catch(() => {
        setServersError(
          'Error fetching your servers, please try again later or contact support if the problem persists.'
        );
        setServersLoading(false);
      });

    fetch(SCHEDULE_LIST_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: JSON.stringify({ from: 'Shared', to: 'PremiumHosting' }) }),
    })
      .then((r) => r.json())
      .then((data) => {
        setExistingMigrations(data.result?.list || []);
        setScheduleLoading(false);
      })
      .catch(() => {
        setScheduleError(
          'Error fetching available dates, please try again later or contact support if the problem persists.'
        );
        setScheduleLoading(false);
      });
  }, [isOpen]);

  // Set defaults when data loads
  useEffect(() => {
    if (availableDates.length > 0 && !selectedDate) {
      setSelectedDate(formatDateValue(availableDates[0]));
    }
  }, [availableDates, selectedDate]);

  useEffect(() => {
    /* v8 ignore start -- hours are always available (0-23 minus taken) */
    if (availableHours.length > 0) {
      setSelectedHour(String(availableHours[0]));
    }
    /* v8 ignore stop */
  }, [availableHours]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const selectedServerData = useMemo(
    () => servers.find((s) => s.oid === selectedServer),
    [servers, selectedServer]
  );

  const handleServerChange = useCallback(
    (name: string) => {
      const server = servers.find((s) => s.name === name);
      setSelectedServer(server!.oid);
    },
    [servers]
  );

  const handleSubmit = useCallback(async () => {
    const nonWindowsSites = migratableSites.filter((s) => !s.windows);
    setSubmitting(true);
    setSubmitError(null);

    const scheduleDate = `${selectedDate} ${formatHour(Number(selectedHour))}:00`;

    try {
      const response = await fetch(SCHEDULE_POST_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          data: JSON.stringify({
            from: 'Shared',
            to: 'PremiumHosting',
            from_oid_list: nonWindowsSites,
            to_oid: selectedServer,
            schedule_timestamp: scheduleDate,
          }),
        }),
      });

      const data = await response.json();
      if (data.result?.success > 0) {
        setSuccess(true);
        onMigrationScheduled();
      } else {
        setSubmitError(data.result?.message || 'Failed to schedule migration.');
      }
    } catch {
      setSubmitError('An unknown error occurred, please contact support if the problem persists.');
    }
    setSubmitting(false);
  }, [migratableSites, selectedServer, selectedDate, selectedHour, onMigrationScheduled]);

  const hasNonWindowsSites = migratableSites.some((s) => !s.windows);
  const allLoaded =
    !serversLoading && !scheduleLoading && !serversError && !scheduleError && hasNonWindowsSites;

  const handleClose = () => {
    setShowSites(false);
    setSelectedDate('');
    setSelectedHour('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Migrate to your Premium Hosting"
      size="large"
    >
      {success ? (
        <Text variant="heading-2">Your migration has been scheduled</Text>
      ) : (
        <Flex direction="column" gap="md">
          <Text variant="body" inline>
            When you migrate a site to Premium Hosting your site will be offline when the migration
            occurs. The duration of this depends on the size of your website.
          </Text>

          <div>
            <Text variant="heading-4" className="hi-ui-manage-packages__migrate-h4">
              Important info:
            </Text>
            <Text variant="body">
              If your sites are currently on our nameservers, we will automatically update your
              domain&rsquo;s DNS to point to your Premium Hosting server upon successful migration.
              If you are using external DNS, you will need to update your domain&rsquo;s DNS to
              point to the server IP listed below.
            </Text>
            <Text variant="body" inline bold style={{ marginBottom: '0.5rem' }}>
              Please note: This will automatically cancel your existing shared hosting.
            </Text>
          </div>

          <Card variant="surface" padding="md" className="hi-ui-manage-packages__migrate-card">
            <Flex direction="column" gap="md">
              {/* Sites section */}
              <div>
                {windowsSites.length > 0 && (
                  <Notice variant="warning">
                    You have selected at least 1 windows package that is unsupported for a migration
                    to premium hosting. Any windows packages selected will be skipped.
                  </Notice>
                )}
                {selectedPackages.length > MAX_SITES && (
                  <Notice variant="warning">
                    You are only allowed to migrate {MAX_SITES} sites at a time.
                  </Notice>
                )}
                <Text variant="heading-4">
                  {migratableSites.length > 1 ? (
                    <>
                      {migratableSites.length} Sites selected{' '}
                      <button
                        type="button"
                        className="hi-ui-manage-packages__faux-link"
                        onClick={() => setShowSites(!showSites)}
                      >
                        (which sites?)
                      </button>
                    </>
                  ) : (
                    'Site selected'
                  )}
                </Text>
                {(showSites || migratableSites.length === 1) && (
                  <ul className="hi-ui-manage-packages__migrate-sites-list">
                    {migratableSites.map((site) => (
                      <li key={site.oid}>
                        {site.windows ? (
                          <>
                            <span style={{ textDecoration: 'line-through' }}>{site.domain}</span>{' '}
                            (Windows packages are unsupported and cannot be migrated to Premium
                            Hosting)
                          </>
                        ) : (
                          site.domain
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Server section */}
              <div>
                <Text variant="heading-4">Migrate to:</Text>
                {serversError ? (
                  <Text variant="error">{serversError}</Text>
                ) : serversLoading ? (
                  <Flex direction="row" gap="sm" align="center">
                    <Text variant="body" inline>
                      Fetching your servers...
                    </Text>
                    <Spinner size="small" />
                  </Flex>
                ) : (
                  <Flex direction="column" gap="sm">
                    <Select
                      size="sm"
                      value={selectedServerData?.name || ''}
                      onChange={(e) => handleServerChange(e.target.value)}
                      disabled={!hasNonWindowsSites}
                    >
                      {servers.map((s) => (
                        <option key={s.oid} value={s.name}>
                          {s.name}
                        </option>
                      ))}
                    </Select>
                    {selectedServerData?.ip_address?.[0] && (
                      <Text variant="body-small">
                        Server IP: {selectedServerData.ip_address[0]}
                      </Text>
                    )}
                  </Flex>
                )}
              </div>

              {/* Schedule section */}
              <div>
                <Text variant="heading-4">When would you like your sites migrated?</Text>
                {scheduleError ? (
                  <Text variant="error">{scheduleError}</Text>
                ) : scheduleLoading ? (
                  <Flex direction="row" gap="sm" align="center">
                    <Text variant="body" inline>
                      Fetching available dates...
                    </Text>
                    <Spinner size="small" />
                  </Flex>
                ) : (
                  <Flex
                    direction="row"
                    gap="sm"
                    className="hi-ui-manage-packages__migrate-schedule"
                  >
                    <Select
                      size="sm"
                      value={selectedHour}
                      onChange={(e) => setSelectedHour(e.target.value)}
                      disabled={!hasNonWindowsSites}
                      aria-label="Migration hour"
                    >
                      {availableHours.map((h) => (
                        <option key={h} value={h}>
                          {formatHour(h)}
                        </option>
                      ))}
                    </Select>
                    <Select
                      size="sm"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      disabled={!hasNonWindowsSites}
                      aria-label="Migration date"
                    >
                      {availableDates.map((d) => (
                        <option key={formatDateValue(d)} value={formatDateValue(d)}>
                          {formatDate(d)}
                        </option>
                      ))}
                    </Select>
                  </Flex>
                )}
              </div>

              {submitError && <Notice variant="error">{submitError}</Notice>}

              <Flex direction="row" gap="sm">
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleSubmit}
                  disabled={!allLoaded || submitting}
                  loading={submitting}
                >
                  Schedule migration
                </Button>
              </Flex>
            </Flex>
          </Card>
        </Flex>
      )}
    </Modal>
  );
};
